/**
 * WHICH NOTIFICATIONS DESERVE A POP-UP — decided from the INBOX, not from the stream.
 *
 * The live `notice` frame used to be the only thing that popped one up, so anything that arrived
 * while the stream was down (a laptop asleep, a proxy timeout — the bell's red dot) reached the
 * bell on the next read and never interrupted anybody. Now the rule reads whatever the inbox
 * query returns, however it was refreshed — a live frame, the reconnect's reconciling read, or
 * the faster poll while the stream is down — so all three paths pop up the same thing, once.
 *
 *   FIRST READ OF A SESSION   sets the line and pops nothing: what was already waiting is the
 *                             bell's job (the badge says so), and five toasts at sign-in for
 *                             yesterday's assignments is noise, not news.
 *   EVERY READ AFTER          pops each UNREAD notice newer than the line, oldest first, at most
 *                             `MAX_POPUPS`, then moves the line. Read elsewhere already? Not news.
 *
 * `createdAt` is the server's ISO-8601 UTC string, so string order is time order and the line
 * never mixes the server's clock with this machine's.
 */
export const MAX_POPUPS = 3;

export interface NoticeLike {
  id: string;
  createdAt: string;
  readAt: string | null;
}

export function freshNotices<T extends NoticeLike>(
  rows: readonly T[],
  line: string | null,
): { popups: T[]; line: string } {
  const newest = rows.reduce((m, r) => (r.createdAt > m ? r.createdAt : m), line ?? '');
  if (line === null) return { popups: [], line: newest };
  const popups = rows
    .filter((r) => r.readAt === null && r.createdAt > line)
    .sort((a, b) => (a.createdAt < b.createdAt ? -1 : 1))
    .slice(-MAX_POPUPS);
  return { popups, line: newest };
}
